import { useRef, useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { isReadOnly } from '../lib/permissions';
import { date, money } from '../lib/format';
import { errorMessage } from '../lib/api-error';
import {
  NATURE_LABELS,
  PURCHASE_NATURES,
  PURCHASE_REGIMES,
  REGIME_HINTS,
  REGIME_LABELS,
  RESTRICTION_CAPS,
  RESTRICTION_LABELS,
  STATUS_LABELS,
  VAT_RESTRICTED_CATEGORIES,
  downloadPurchaseAttachment,
  isReverseChargeRegime,
  useConfirmPurchase,
  useCreatePurchase,
  useDeletePurchase,
  usePurchases,
  useReopenPurchase,
  useUploadPurchaseAttachment,
  type PurchaseLine,
  type PurchaseNature,
  type PurchaseRegime,
  type PurchaseInvoice,
  type PurchaseStatus,
  type VatRestrictedCategory,
} from '../lib/purchases-api';
import { usePostDocument } from '../lib/accounting-api';
import { saveBlob } from '../lib/einvoice-api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Spinner,
  Table,
  Td,
  Th,
} from '../components/ui';

/**
 * ALIŞ FATURALARI.
 *
 * ⚠ EKRAN İKİ EKSENİ AYRI SORAR ve bu kozmetik değil: KDV indirimi ile GELİR
 * VERGİSİ indirimi farklı yüzdelerdir. Bir restoran faturasında KDV'nin
 * indirilebilir kısmı SIFIRDIR ama gider olarak düşülebilen kısım %69'dur.
 * Tek bir "indirilebilirlik" alanı o iki gerçeği tek sayıya sıkıştırır ve
 * plan bu karıştırmayı "en sık yapılan muhasebe hatası" diye niteliyor.
 *
 * ⚠ KISIT KATEGORİSİ SEÇİLİNCE KDV ORANI TAVANA ÇEKİLİR — ama KİLİTLENMEZ.
 * Md. 45 §2 "%50'yi hiçbir hâlde aşamaz" diyor; bu bir TAVANDIR, taban değil.
 * Gerçek profesyonel kullanım daha düşükse operatör aşağı inebilmeli.
 */

const PAGE_SIZE = 25;

type StatusFilter = PurchaseStatus | 'all';

interface DraftLine {
  description: string;
  baseExclVat: string;
  vatRate: string;
  purchaseNature: PurchaseNature;
  regime: PurchaseRegime;
  vatDeductiblePct: string;
  incomeTaxDeductiblePct: string;
  vatRestrictionCategory: VatRestrictedCategory | '';
}

/**
 * ⚠ "BENZERİNİ OLUŞTUR" NE KOPYALAR, NE KOPYALAMAZ. Her ay aynı tedarikçiden
 * gelen fatura (hosting, SaaS) için tedarikçi, satır açıklamaları, nitelik,
 * rejim ve oranlar aynıdır; belge numarası, tarihler ve TUTARLAR her belgede
 * yenidir ve bilerek BOŞ gelir. Tutarı kopyalamak, geçen ayın rakamının bu
 * ayın faturası diye gözden kaçmasına davet olurdu.
 */
function linesFromTemplate(template: PurchaseInvoice | null): DraftLine[] {
  const source = template?.lines ?? [];
  if (source.length === 0) return [emptyLine()];
  return source.map((l) => ({
    description: l.description,
    baseExclVat: '',
    vatRate: l.vatRate,
    purchaseNature: l.purchaseNature,
    regime: l.regime,
    vatDeductiblePct: l.vatDeductiblePct ?? '100',
    incomeTaxDeductiblePct: l.incomeTaxDeductiblePct ?? '100',
    vatRestrictionCategory: l.vatRestrictionCategory ?? '',
  }));
}

function emptyLine(): DraftLine {
  return {
    description: '',
    baseExclVat: '',
    vatRate: '21.00',
    purchaseNature: 'SERVICES_AND_OTHER_GOODS',
    regime: 'DOMESTIC',
    vatDeductiblePct: '100',
    incomeTaxDeductiblePct: '100',
    vatRestrictionCategory: '',
  };
}

export function PurchasesPage() {
  const { user } = useAuth();
  const readOnly = isReadOnly(user);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [template, setTemplate] = useState<PurchaseInvoice | null>(null);

  const list = usePurchases({
    page,
    pageSize: PAGE_SIZE,
    status: status === 'all' ? undefined : status,
    q: q.trim() || undefined,
  });

  return (
    <>
      <PageHeader
        title="Alış faturaları"
        description="Tedarikçiden alınan belgeler. Numara tedarikçinindir; biz numara vermiyoruz."
        actions={
          readOnly ? null : (
            <Button
              onClick={() => {
                setTemplate(null);
                setOpen((v) => !v);
              }}
            >
              {open ? 'Vazgeç' : 'Yeni alış'}
            </Button>
          )
        }
      />

      {/* ⚠ `key` şablon değişince formu SIFIRLAR: aksi hâlde ikinci "Benzerini
          oluştur" tıklaması ilk belgenin alanlarıyla açılırdı. */}
      {open && !readOnly && (
        <PurchaseForm
          key={template?.id ?? 'new'}
          template={template}
          onDone={() => {
            setOpen(false);
            setTemplate(null);
          }}
        />
      )}

      <Card className="mt-4">
        <CardHeader title="Belgeler" />
        <div className="flex flex-wrap gap-2 px-4 py-3">
          <Select
            label="Durum"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as StatusFilter);
              setPage(1);
            }}
          >
            <option value="all">Hepsi</option>
            <option value="DRAFT">Taslak</option>
            <option value="CONFIRMED">Onaylandı</option>
          </Select>
          <Input
            label="Ara"
            value={q}
            placeholder="Tedarikçi ya da belge numarası"
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {list.isPending ? (
          <div className="px-4 py-6">
            <Spinner />
          </div>
        ) : list.isError ? (
          <ErrorState message={errorMessage(list.error)} />
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Kayıt yok" description="Henüz alış belgesi girilmemiş." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Tedarikçi</Th>
                <Th>Belge no</Th>
                <Th>Tarih</Th>
                <Th align="right">Matrah</Th>
                <Th align="right">KDV</Th>
                <Th align="right">İndirilebilir</Th>
                <Th>Belge</Th>
                <Th>Durum</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((p) => (
                <PurchaseRow
                  key={p.id}
                  purchase={p}
                  readOnly={readOnly}
                  onDuplicate={() => {
                    setTemplate(p);
                    setOpen(true);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              ))}
            </tbody>
          </Table>
        )}

        {/*
          ⚠ SAYFALAMA YOKTU ve sayfa durumu vardı: 25. belgeden sonrası yalnız
          aramayla bulunabiliyordu. "Ekini yüklemediğim alış hangisiydi?"
          sorusu böylece cevapsız kalıyordu.
        */}
        {(list.data?.total ?? 0) > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 text-xs text-[var(--c-ink-soft)]">
            <span>
              {list.data?.total} kayıt · sayfa {page}
            </span>
            <div className="flex gap-2">
              <Button variant="ghost" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                Önceki
              </Button>
              <Button
                variant="ghost"
                disabled={page * PAGE_SIZE >= (list.data?.total ?? 0)}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

/**
 * Alış belgesi satırı.
 *
 * ⚠ "DEFTERE KAYDET" AYRI BİR ADIM ve onaydan otomatik akmıyor. Sunucuda kayıt,
 * belge işleminin transaction'ı DIŞINDA tutuluyor: fatura kesme/onaylama
 * numara sayacının satır kilidini COMMIT'e kadar tutuyor ve oraya İKİNCİ bir
 * sayaç (yevmiye numarası) sokmak deadlock riski açardı. Ayrıca kayıt hatası
 * belgenin kendisini geri sardırırdı — oysa belge yasal olarak vardır.
 *
 * Ayrılığın bedeli unutulabilmesidir; karşılığı Defter ekranındaki "deftere
 * girmemiş belgeler" sayacıdır.
 */
function PurchaseRow({
  purchase,
  readOnly,
  onDuplicate,
}: {
  purchase: PurchaseInvoice;
  readOnly: boolean;
  onDuplicate: () => void;
}) {
  const postToLedger = usePostDocument();
  const upload = useUploadPurchaseAttachment();
  const confirmPurchase = useConfirmPurchase(purchase.id);
  const reopenPurchase = useReopenPurchase(purchase.id);
  const deletePurchase = useDeletePurchase();
  const fileInput = useRef<HTMLInputElement>(null);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /*
   * ⚠ ONAY SATIRDAN DA YAPILABİLMELİ. Önceden onay YALNIZ oluşturma formunun
   * içindeydi: form kapandıktan sonra taslak bir daha asla onaylanamıyordu —
   * ne düzenlenebiliyor, ne silinebiliyor. Kaydedilmiş ama beyana hiç
   * girmeyen, görünmez bir belge demekti.
   */
  const [confirming, setConfirming] = useState(false);
  const [documentVat, setDocumentVat] = useState('');

  /*
   * ⚠ BELGEDE YAZAN KDV İLE SATIRLARDAN BEKLENEN AYRIŞTIYSA SÖYLENİR — ama
   * ENGELLENMEZ: indirilecek KDV tedarikçinin belgesinde yazan tutardır ve
   * tedarikçinin yuvarlaması bizimkinden bir kuruş farklı olabilir. Susmak ise
   * 10,50 yerine 105,00 yazılmasını görünmez kılıyordu.
   */
  const suggested = purchase.suggestedVat;
  const reverseCharge = (purchase.lines ?? []).some((l) => isReverseChargeRegime(l.regime));
  const mismatch =
    purchase.status === 'CONFIRMED' &&
    suggested !== undefined &&
    Math.abs(Number(purchase.totalVat) - Number(suggested)) > 0.02;

  const onFile = (file: File | undefined): void => {
    if (!file) return;
    setError(null);
    void upload
      .mutateAsync({ id: purchase.id, file })
      .catch((e: unknown) => setError(errorMessage(e)));
  };

  const download = (): void => {
    setError(null);
    setDownloading(true);
    void downloadPurchaseAttachment(purchase.id)
      .then((file) => saveBlob(file.blob, file.fileName ?? `alis-${purchase.id}`))
      .catch((e: unknown) => setError(errorMessage(e)))
      .finally(() => setDownloading(false));
  };

  return (
    <>
      <tr>
        <Td>{purchase.supplierName}</Td>
        <Td className="font-mono text-xs">{purchase.supplierDocumentNumber}</Td>
        <Td>{date(purchase.invoiceDate)}</Td>
        <Td align="right">{money(purchase.totalExclVat)}</Td>
        <Td align="right">
          {money(purchase.totalVat)}
          {mismatch && (
            <span className="block text-xs text-[var(--c-warn)]">
              ⚠ satırlardan beklenen {money(suggested)}
            </span>
          )}
        </Td>
        {/*
          ⚠ İNDİRİLEBİLİR KDV AYRI SÜTUN. Toplam KDV ile aynı sanılırsa
          operatör kutu 59'a gireceği rakamı yanlış okur; restoran
          faturasında ikisi 21,00 ve 0,00'dır.
        */}
        <Td align="right">{money(purchase.deductibleVat)}</Td>
        {/*
          ⚠ EK, KAYDIN DAYANAĞIDIR: satırımız tedarikçi belgesinin kopyasıdır.
          Eksik olduğu burada görünmeli — denetimde istenen o belgedir.
        */}
        <Td>
          {purchase.attachmentStoragePath ? (
            <Badge tone="ok">var</Badge>
          ) : (
            <Badge tone="warn">yok</Badge>
          )}
        </Td>
        <Td>
          <Badge tone={purchase.status === 'CONFIRMED' ? 'ok' : 'neutral'}>
            {STATUS_LABELS[purchase.status]}
          </Badge>
        </Td>
        <Td>
          <div className="flex gap-1.5">
            {purchase.attachmentStoragePath ? (
              <Button variant="ghost" disabled={downloading} onClick={download}>
                Belgeyi indir
              </Button>
            ) : (
              !readOnly && (
                <>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png"
                    className="hidden"
                    onChange={(ev) => {
                      const file = ev.target.files?.[0];
                      // Aynı dosya yeniden seçilebilsin (başarısız yüklemeden sonra).
                      ev.target.value = '';
                      onFile(file);
                    }}
                  />
                  <Button
                    variant="ghost"
                    disabled={upload.isPending}
                    onClick={() => fileInput.current?.click()}
                  >
                    Belge yükle
                  </Button>
                </>
              )
            )}
            {!readOnly && (
              <Button variant="ghost" onClick={onDuplicate}>
                Benzerini oluştur
              </Button>
            )}
            {!readOnly && purchase.status === 'DRAFT' && (
              <>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setError(null);
                    // ⚠ Alan ÖNERİYLE dolu açılır; operatör belgedekiyle karşılaştırır.
                    setDocumentVat(suggested ?? '');
                    setConfirming((v) => !v);
                  }}
                >
                  {confirming ? 'Vazgeç' : 'Onayla'}
                </Button>
                <Button
                  variant="ghost"
                  disabled={deletePurchase.isPending}
                  onClick={() => {
                    setError(null);
                    if (!window.confirm('Bu TASLAK belge silinsin mi? Geri alınamaz.')) return;
                    deletePurchase
                      .mutateAsync(purchase.id)
                      .catch((e: unknown) => setError(errorMessage(e)));
                  }}
                >
                  Sil
                </Button>
              </>
            )}
            {!readOnly && purchase.status === 'CONFIRMED' && (
              <>
                <Button
                  variant="ghost"
                  disabled={postToLedger.isPending}
                  onClick={() => {
                    setError(null);
                    postToLedger
                      .mutateAsync({ sourceType: 'PURCHASE_INVOICE', sourceId: purchase.id })
                      .catch((e: unknown) => setError(errorMessage(e)));
                  }}
                >
                  Deftere kaydet
                </Button>
                {/*
                  ⚠ GERİ AÇMA ALIŞTA MEŞRUDUR: mali gerçek tedarikçinin
                  belgesidir, bizim kaydımız onun kopyasıdır ve yanlış yazılmış
                  bir kopyayı düzeltmek tahrifat değildir.
                */}
                <Button
                  variant="ghost"
                  disabled={reopenPurchase.isPending}
                  onClick={() => {
                    setError(null);
                    reopenPurchase
                      .mutateAsync()
                      .catch((e: unknown) => setError(errorMessage(e)));
                  }}
                >
                  Taslağa aç
                </Button>
              </>
            )}
          </div>
        </Td>
      </tr>

      {confirming && !readOnly && (
        <tr>
          <Td colSpan={9}>
            <div className="flex flex-wrap items-end gap-2 py-2">
              <Input
                label={documentVatLabel(reverseCharge)}
                hint={documentVatHint(reverseCharge, suggested)}
                value={documentVat}
                onChange={(e) => setDocumentVat(e.target.value)}
              />
              <Button
                disabled={confirmPurchase.isPending}
                onClick={() => {
                  setError(null);
                  confirmPurchase
                    .mutateAsync(documentVat)
                    .then(() => setConfirming(false))
                    .catch((e: unknown) => setError(errorMessage(e)));
                }}
              >
                Onayla ve kutuları dondur
              </Button>
            </div>
          </Td>
        </tr>
      )}
      {error && (
        <tr>
          <Td colSpan={9}>
            <p className="text-xs text-[var(--c-danger)]">{error}</p>
          </Td>
        </tr>
      )}
    </>
  );
}

/*
 * ⚠⚠ TERS YÜKÜMLÜLÜKTE ONAY ALANI "BELGEDE YAZAN KDV" DEĞİLDİR. Tedarikçinin
 * faturasında KDV 0 yazar; alana BİZİM beyan ettiğimiz vergi girilir ve kutu
 * 55/56 ile 59 ondan dolar. Etiket bir süre her rejimde "belgede yazan" diyordu
 * ve öneri sıfırdı: onaylanan AB hizmet alımları 88'i doldurup 55/59'u boş
 * bıraktı (canlıda bir Hetzner faturasıyla ölçüldü).
 */
function documentVatLabel(reverseCharge: boolean): string {
  return reverseCharge ? 'Beyan edeceğiniz KDV (ters yükümlülük)' : 'Belgede yazan toplam KDV';
}

function documentVatHint(reverseCharge: boolean, suggested: string | undefined): string {
  return reverseCharge
    ? `Tedarikçinin faturasında 0 yazar — bu alana matrah × oran girilir: ${money(suggested)}. Aynı tutar hem ödenecek (55/56) hem indirilecek (59) olarak yazılır; net etki sıfırdır.`
    : `Satırlardan beklenen: ${money(suggested)} — belgedekiyle karşılaştırın. Nokta ile: 10.50`;
}

function PurchaseForm({
  template,
  onDone,
}: {
  template: PurchaseInvoice | null;
  onDone: () => void;
}) {
  const create = useCreatePurchase();
  const [created, setCreated] = useState<string | null>(null);
  const confirm = useConfirmPurchase(created ?? '');
  const reopen = useReopenPurchase(created ?? '');

  const [supplierName, setSupplierName] = useState(template?.supplierName ?? '');
  const [supplierCountry, setSupplierCountry] = useState(template?.supplierCountry ?? 'BE');
  const [supplierVatNumber, setSupplierVatNumber] = useState(template?.supplierVatNumber ?? '');
  const [supplierDocumentNumber, setDocNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [vatChargeableAt, setVatChargeableAt] = useState('');
  const [documentVat, setDocumentVat] = useState('');
  const [suggestedVat, setSuggestedVat] = useState<string | undefined>(undefined);
  const [lines, setLines] = useState<DraftLine[]>(() => linesFromTemplate(template));
  const reverseCharge = lines.some((l) => isReverseChargeRegime(l.regime));
  const [error, setError] = useState<string | null>(null);

  const setLine = (i: number, patch: Partial<DraftLine>): void => {
    setLines((prev) => prev.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  };

  /*
   * ⚠ KISIT SEÇİLİNCE ORAN TAVANA ÇEKİLİR, KİLİTLENMEZ. Alan hâlâ
   * düzenlenebilir çünkü tavan bir TABAN değildir: gerçek profesyonel kullanım
   * daha düşükse operatör aşağı inebilmeli (md. 45 §1). Sunucu yalnız YUKARI
   * çıkmayı reddediyor.
   */
  const setRestriction = (i: number, value: VatRestrictedCategory | ''): void => {
    const patch: Partial<DraftLine> = { vatRestrictionCategory: value };
    if (value !== '') patch.vatDeductiblePct = String(RESTRICTION_CAPS[value]);
    setLine(i, patch);
  };

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      const body = {
        supplierName,
        supplierCountry,
        // ⚠ KDV numarası mükerrer korumanın ikinci anahtarıdır (kısmi UNIQUE).
        ...(supplierVatNumber.trim() === '' ? {} : { supplierVatNumber: supplierVatNumber.trim() }),
        supplierDocumentNumber,
        invoiceDate,
        // ⚠ BOŞSA HİÇ GÖNDERİLMEZ: `''` göndermek tarih desenine takılır
        // (`@Matches(DATE)`) ve operatör "neden kaydedilmedi" sorusuyla kalır.
        ...(vatChargeableAt === '' ? {} : { vatChargeableAt }),
        lines: lines.map((l): PurchaseLine => ({
          description: l.description,
          baseExclVat: l.baseExclVat,
          vatRate: l.vatRate,
          purchaseNature: l.purchaseNature,
          regime: l.regime,
          vatDeductiblePct: l.vatDeductiblePct,
          incomeTaxDeductiblePct: l.incomeTaxDeductiblePct,
          ...(l.vatRestrictionCategory === ''
            ? {}
            : { vatRestrictionCategory: l.vatRestrictionCategory }),
        })),
      };
      const saved = await create.mutateAsync(body);
      setCreated(saved.id);
      // ⚠ Öneri SUNUCUDAN gelir; panel tutar hesaplamaz.
      setSuggestedVat(saved.suggestedVat);
      setDocumentVat(saved.suggestedVat ?? '');
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  const doConfirm = async (): Promise<void> => {
    setError(null);
    try {
      await confirm.mutateAsync(documentVat);
      onDone();
    } catch (e) {
      setError(errorMessage(e));
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader
        title={
          created
            ? 'Taslak kaydedildi — onaylayın'
            : template
              ? `Yeni alış — ${template.supplierName} benzeri`
              : 'Yeni alış belgesi'
        }
        description={
          created
            ? reverseCharge
              ? 'Ters yükümlülük: tedarikçinin faturasında KDV 0 yazar. Alana beyan edeceğiniz KDV önerildi (matrah × oran); kontrol edip onaylayın.'
              : 'Belgede YAZAN toplam KDV’yi girin. Matrahtan yeniden hesaplamıyoruz: indirilecek KDV, tedarikçinin belgesinde yazanla sınırlıdır.'
            : template
              ? 'Tedarikçi ve satırlar önceki belgeden geldi. Belge numarasını, tarihleri ve tutarları bu faturadan girin.'
              : 'Numara tedarikçinindir; sayaç yoktur.'
        }
      />
      <div className="space-y-3 px-4 py-3">
        {error && <ErrorState message={error} />}

        {created === null ? (
          <>
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                label="Tedarikçi adı"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
              />
              <Input
                label="Ülke (2 harf)"
                value={supplierCountry}
                onChange={(e) => setSupplierCountry(e.target.value.toUpperCase())}
              />
              <Input
                label="Tedarikçinin KDV numarası (isteğe bağlı)"
                hint="Faturada yazar, ör. DE812871812. Aynı belgenin iki kez girilmesine karşı korur."
                value={supplierVatNumber}
                onChange={(e) => setSupplierVatNumber(e.target.value.toUpperCase())}
              />
              <Input
                label="Belge numarası"
                value={supplierDocumentNumber}
                onChange={(e) => setDocNo(e.target.value)}
              />
              <Input
                label="Fatura tarihi"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
              />
              {/* ⚠ KDV DÖNEMİNİ BU ALAN BELİRLER. Boş bırakılırsa fatura
                  tarihi kullanılır — bu bir hukuki türetme DEĞİL, en spesifik
                  tarihin kullanılmasıdır. Alan sunucuda M3'ten beri vardı ama
                  hiçbir DTO'da olmadığı için SORULAMIYORDU. */}
              <Input
                label="KDV muacceliyet tarihi (boşsa fatura tarihi)"
                type="date"
                hint={
                  reverseCharge
                    ? 'Yurtdışı hizmette genelde hizmetin verildiği dönemin SON GÜNÜ (ör. faturada "09/2026" → 2026-09-30). KDV dönemini bu alan belirler; karar sizin.'
                    : undefined
                }
                value={vatChargeableAt}
                onChange={(e) => setVatChargeableAt(e.target.value)}
              />
            </div>

            {lines.map((l, i) => (
              <div key={i} className="rounded border border-[var(--c-line)] p-3">
                <div className="grid gap-2 sm:grid-cols-2">
                  <Input
                    label="Açıklama"
                    value={l.description}
                    onChange={(e) => setLine(i, { description: e.target.value })}
                  />
                  {/* ⚠ Ondalık ayracı NOKTA (sunucu virgülü reddediyor) —
                      Belçika klavyesinde refleks virgüldür ve reddin sebebi
                      hiçbir alanda görünmüyordu. */}
                  <Input
                    label="Matrah (KDV hariç)"
                    hint="Nokta ile: 50.00"
                    value={l.baseExclVat}
                    onChange={(e) => setLine(i, { baseExclVat: e.target.value })}
                  />
                  {/* ⚠ ORAN YALNIZ BEKLENEN KDV'Yİ ÖNERMEK İÇİN kullanılıyor;
                      indirilecek KDV belgede yazan tutardan gelir. */}
                  <Input
                    label="KDV oranı"
                    hint="Onay alanına önerilecek tutar bundan hesaplanır."
                    value={l.vatRate}
                    onChange={(e) => setLine(i, { vatRate: e.target.value })}
                  />

                  <Select
                    label="Alış niteliği"
                    value={l.purchaseNature}
                    onChange={(e) =>
                      setLine(i, { purchaseNature: e.target.value as PurchaseNature })
                    }
                    hint="Matrah kutusunu belirler."
                  >
                    {PURCHASE_NATURES.map((n) => (
                      <option key={n} value={n}>
                        {NATURE_LABELS[n]}
                      </option>
                    ))}
                  </Select>

                  <Select
                    label="KDV rejimi"
                    value={l.regime}
                    onChange={(e) => setLine(i, { regime: e.target.value as PurchaseRegime })}
                    hint={REGIME_HINTS[l.regime]}
                  >
                    {PURCHASE_REGIMES.map((r) => (
                      <option key={r} value={r}>
                        {REGIME_LABELS[r]}
                      </option>
                    ))}
                  </Select>

                  <Select
                    label="KDV kısıtı"
                    value={l.vatRestrictionCategory}
                    onChange={(e) =>
                      setRestriction(i, e.target.value as VatRestrictedCategory | '')
                    }
                    hint="Boş bırakmak geçerli bir cevaptır: harcamaların çoğu hiçbir kısıta girmez."
                  >
                    <option value="">Kısıt yok</option>
                    {VAT_RESTRICTED_CATEGORIES.map((k) => (
                      <option key={k} value={k}>
                        {RESTRICTION_LABELS[k]}
                      </option>
                    ))}
                  </Select>

                  {/*
                    ⚠ İKİ EKSEN YAN YANA VE AYRI ETİKETLİ. Aynı harcama iki
                    eksende zıt sonuç verir: restoran KDV’de %0, gelir
                    vergisinde %69. Tek alan bu iki gerçeği tek sayıya
                    sıkıştırırdı.
                  */}
                  <Input
                    label="KDV indirilebilirliği (%)"
                    value={l.vatDeductiblePct}
                    onChange={(e) => setLine(i, { vatDeductiblePct: e.target.value })}
                    hint={
                      l.vatRestrictionCategory === ''
                        ? 'Beyandaki kutu 59’a giden pay.'
                        : `Hüküm en çok %${RESTRICTION_CAPS[l.vatRestrictionCategory]} veriyor — bu bir TAVAN, aşağı inebilirsiniz.`
                    }
                  />
                  <Input
                    label="Gelir vergisi indirimi (%)"
                    value={l.incomeTaxDeductiblePct}
                    onChange={(e) => setLine(i, { incomeTaxDeductiblePct: e.target.value })}
                    hint="KDV beyanıyla İLGİSİ YOKTUR. ⚠ Bu oranlar birincil kaynaktan doğrulanmadı."
                  />
                </div>
              </div>
            ))}

            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setLines((p) => [...p, emptyLine()])}>
                Satır ekle
              </Button>
              <Button onClick={() => void submit()} disabled={create.isPending}>
                Taslağı kaydet
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-wrap items-end gap-2">
            <Input
              label={documentVatLabel(reverseCharge)}
              hint={documentVatHint(reverseCharge, suggestedVat)}
              value={documentVat}
              onChange={(e) => setDocumentVat(e.target.value)}
            />
            <Button onClick={() => void doConfirm()} disabled={confirm.isPending}>
              Onayla
            </Button>
            {/*
              ⚠ BU DÜĞME SESSİZCE ÖLÜYDÜ: `.catch` yoktu ve taslak hâldeki bir
              kayıtta `PURCHASE_NOT_CONFIRMED` atıyordu — ekranda hiçbir şey
              olmuyordu. Üstelik düğme tam da "yazdığımda hata vardı, geri
              döneyim" refleksinin bastığı yerde duruyor.

              ⚠ GERİ AÇMA SATIŞ TARAFINDA YOKTUR. Alışta mali gerçek
              tedarikçinin belgesidir; bizim kaydımız onun kopyasıdır ve yanlış
              yazılmış bir kopyayı düzeltmek tahrifat değildir.
            */}
            <Button
              variant="secondary"
              disabled={reopen.isPending}
              onClick={() => {
                setError(null);
                reopen
                  .mutateAsync()
                  .then(onDone)
                  .catch((e: unknown) => setError(errorMessage(e)));
              }}
            >
              Taslağa geri aç
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}
